import type { Anotacao, ConsoleEntry, IntakePayload, Meta, Reporter } from "../types";

export interface DadosReport {
  titulo: string;
  descricao: string;
  /** Tipo pedido (um dos `tiposAdicionais`); ausente = tipo principal do relator. */
  tipo?: string;
  anotacoes: Anotacao[];
  console: ConsoleEntry[];
  meta: Meta;
  reporter?: Reporter;
  maxConsole: number;
}

/** Normaliza o reporter aos tetos do backend (nome 200 / email 320); undefined se
    ficar sem nenhum campo (assim JSON.stringify o omite → report anônimo). */
function normalizarReporter(r: Reporter | undefined): Reporter | undefined {
  if (!r) return undefined;
  const nome = r.nome?.trim().slice(0, 200) || undefined;
  const email = r.email?.trim().slice(0, 320) || undefined;
  return nome || email ? { ...(nome ? { nome } : {}), ...(email ? { email } : {}) } : undefined;
}

/** Monta o payload aplicando os mesmos tetos do backend (título/descrição/
    anotações/console), evitando 422/413. */
export function construirPayload(d: DadosReport): IntakePayload {
  return {
    // Teto do TÍTULO: o servidor exige 80 (mesmo limite de qualquer card). Cortar em 500
    // aqui fazia todo título longo virar um 422 que a pessoa não tinha como prever.
    titulo: d.titulo.slice(0, 80),
    descricao: d.descricao.slice(0, 500),
    // Chave omitida quando não há tipo pedido — `undefined` some no JSON.stringify, e é
    // a AUSÊNCIA que o intake lê como "caminho principal".
    ...(d.tipo ? { tipo: d.tipo } : {}),
    anotacoes: d.anotacoes.slice(0, 100),
    console: d.console.slice(-Math.max(1, d.maxConsole)),
    meta: d.meta,
    reporter: normalizarReporter(d.reporter),
  };
}

/**
 * multipart/form-data: `screenshot` (print) + `anexo` (arquivo que a pessoa escolheu,
 * DEV-211) + `payload` (JSON). O browser define o Content-Type com boundary — NÃO setar à
 * mão.
 *
 * Chave ausente quando não há o arquivo — nos dois casos. Mandar `null` no lugar não seria a
 * mesma coisa: o FormData o converteria na string "null", que reprovaria na regra `file` do
 * servidor e viraria um 422 sem sentido para quem está na tela. (A print é opcional no
 * caminho secundário do DEV-144; o anexo é opcional sempre.)
 *
 * O NOME do anexo viaja: é o que o time lê no painel do card, e "blob" não diz nada. O da
 * print é fixo porque quem a gerou fomos nós.
 */
export function construirFormData(screenshot: Blob | null, payload: IntakePayload, anexo?: File | null): FormData {
  const fd = new FormData();
  if (screenshot) fd.append("screenshot", screenshot, "screenshot.webp");
  if (anexo) fd.append("anexo", anexo, anexo.name);
  fd.append("payload", JSON.stringify(payload));
  return fd;
}
